import React, { useState, useRef, useEffect } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// SelectOrdemTarefas.jsx — r220
// ────────────────────────────────────────────────────────────────────────────
// Campo de ordenação da tela de Tarefas, agora com ícone SVG por opção.
//
// Por que deixou de ser um <select> nativo: o navegador só renderiza TEXTO
// dentro de <option>. Era por isso que as opções usavam emoji (📅 🏢 📁) —
// emoji muda de desenho a cada sistema operacional e não aceita cor. Pra ter
// SVG a lista precisa ser um dropdown próprio, que é o que este componente faz,
// mantendo a mesma altura (28px) e fonte (11px) do select antigo.
//
// Exporta também os ícones das abas Lista/Agenda, que estavam em emoji pelo
// mesmo motivo e aparecem nas duas telas (Lista e Agenda).
//
// Convenção dos ícones: viewBox 0 0 24 24 e `currentColor`, igual aos do card.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul: '#003399', azulBg: '#eff6ff',
  borda: '#e8eaed', texto: '#1a1a2e', muted: '#888', branco: '#fff',
};

const Svg = ({ children, tamanho = 14, cor = 'currentColor' }) => (
  <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none"
       stroke={cor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

// ─── Ícones das abas (exportados: usados na Lista e na Agenda) ──────────────
export const IconeAbaLista = (p) => (
  <Svg {...p}>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <path d="M3 6h.01M3 12h.01M3 18h.01" />
  </Svg>
);
export const IconeAbaAgenda = (p) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Svg>
);

// r220a: lupa do campo de busca. Estava dentro do placeholder ("🔍 Buscar..."),
// que é atributo de texto puro e não aceita markup — por isso o ícone vira um
// SVG posicionado por cima do input, e o placeholder fica só com o texto.
export const IconeBusca = (p) => (
  <Svg {...p}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></Svg>
);

// ─── Ícones das ordenações ─────────────────────────────────────────────────
const SetaBaixo = (p) => (
  <Svg {...p}><path d="M12 5v14" /><path d="M19 12l-7 7-7-7" /></Svg>
);
const SetaCima = (p) => (
  <Svg {...p}><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></Svg>
);
const Bandeira = (p) => (
  <Svg {...p}><path d="M4 15V3h14l-2.5 4L18 11H4" /><path d="M4 22V3" /></Svg>
);
const Prioridade = (p) => (
  <Svg {...p}><path d="M4 20V9M10 20V4M16 20v-7M2 20h20" /></Svg>
);
const Circulo = (p) => (
  <Svg {...p}><circle cx="12" cy="12" r="7" /></Svg>
);
const Calendario = (p) => (
  <Svg {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></Svg>
);
const Predio = (p) => (
  <Svg {...p}>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01M9 15h.01M15 15h.01" />
  </Svg>
);
const Pasta = (p) => (
  <Svg {...p}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></Svg>
);
const Etiqueta = (p) => (
  <Svg {...p}>
    <path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-7.2-7.2a2 2 0 0 1-.6-1.4V4a1 1 0 0 1 1-1h8a2 2 0 0 1 1.4.6l7.4 7.4a2 2 0 0 1 0 2.8z" />
    <path d="M7.5 7.5h.01" />
  </Svg>
);
const Pessoa = (p) => (
  <Svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></Svg>
);
const Pessoas = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2 21v-1a5.5 5.5 0 0 1 5.5-5.5h3A5.5 5.5 0 0 1 16 20v1" />
    <path d="M17 4.2a3.5 3.5 0 0 1 0 6.8M18 14.6A5.5 5.5 0 0 1 22 20v1" />
  </Svg>
);

// Ordem aqui = ordem no dropdown. Os `valor` batem com o mapa ORDENS do
// tarefa.controller (backend) — mudar um lado exige mudar o outro.
export const ORDENS_TAREFA = [
  { valor: 'recente',     label: 'Mais recentes',   Icone: SetaBaixo  },
  { valor: 'antiga',      label: 'Mais antigas',    Icone: SetaCima   },
  { valor: 'deadline',    label: 'Deadline',        Icone: Bandeira   },
  { valor: 'prioridade',  label: 'Prioridade',      Icone: Prioridade },
  { valor: 'status',      label: 'Status',          Icone: Circulo    },
  { valor: 'data_prog',   label: 'Data programada', Icone: Calendario },
  { valor: 'cliente',     label: 'Cliente',         Icone: Predio     },
  { valor: 'projeto',     label: 'Projeto',         Icone: Pasta      },
  { valor: 'setor',       label: 'Setor',           Icone: Etiqueta   },
  { valor: 'solicitante', label: 'Solicitante',     Icone: Pessoa     },
  { valor: 'responsavel', label: 'Responsável',     Icone: Pessoas    },
];

function SelectOrdemTarefas({ valor, onChange }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  // Fecha ao clicar fora (mesmo padrão dos MultiSelect da tela de Acessos)
  useEffect(() => {
    const fora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  const atual = ORDENS_TAREFA.find(o => o.valor === valor) || ORDENS_TAREFA[0];
  const IconeAtual = atual.Icone;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setAberto(a => !a)}
        title={`Ordenar por: ${atual.label}`}
        style={{
          height: '28px', padding: '0 8px',
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          border: `1px solid ${aberto ? COR.azul : COR.borda}`, borderRadius: '4px',
          backgroundColor: COR.branco, color: COR.texto,
          fontSize: '11px', fontWeight: '500',
          fontFamily: "'Manrope', sans-serif", cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <IconeAtual tamanho={13} cor={COR.azul} />
        <span>{atual.label}</span>
        <span style={{ color: COR.muted, fontSize: '9px' }}>{aberto ? '▲' : '▼'}</span>
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: '4px',
          minWidth: '180px', zIndex: 60,
          backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
          borderRadius: '5px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }}>
          {ORDENS_TAREFA.map(o => {
            const Icone = o.Icone;
            const ativo = o.valor === valor;
            return (
              <button
                key={o.valor}
                type="button"
                onClick={() => { onChange(o.valor); setAberto(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', padding: '7px 10px',
                  border: 'none', textAlign: 'left', cursor: 'pointer',
                  backgroundColor: ativo ? COR.azulBg : 'transparent',
                  color: ativo ? COR.azul : COR.texto,
                  fontSize: '11px', fontWeight: ativo ? '700' : '500',
                  fontFamily: "'Manrope', sans-serif",
                }}
                onMouseEnter={e => { if (!ativo) e.currentTarget.style.backgroundColor = COR.azulBg; }}
                onMouseLeave={e => { if (!ativo) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icone tamanho={14} cor={ativo ? COR.azul : COR.muted} />
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SelectOrdemTarefas;
